const CONTENT_TYPE = 'text/plain;charset=UTF-8';
const DWELL_MS = 500;
const FLUSH_MS = 10_000;
const REQUEST_TIMEOUT_MS = 10_000;
const RETRY_MS = 30_000;
const BATCH_SIZE = 16;
const QUEUE_LIMIT = 64;
const MAX_BODY_BYTES = 32 * 1024;
const KEEPALIVE_BYTES = 48 * 1024;
const ID_PATTERN = /^[1-9][0-9]{0,31}$/;
const SLUG_PATTERN = /^[a-z0-9-]{1,255}$/;
const PAGE_PATTERN = /^\/[A-Za-z0-9\-_./%]*$/;
const PAGE_MAX_LENGTH = 512;
const NAME_MAX_LENGTH = 500;

function devLog(window, env, kind, action, details) {
  if (env !== 'dev') return;
  try {
    window.console?.log?.(`[Aest impressions][${kind}] ${action}`, { env, ...details });
  } catch {
    // Logging must not interrupt collection or delivery.
  }
}

export function resolvePage(window) {
  const raw = window.location?.pathname || '/';
  if (
    typeof raw === 'string' &&
    raw.length >= 1 &&
    raw.length <= PAGE_MAX_LENGTH &&
    PAGE_PATTERN.test(raw)
  ) {
    return raw;
  }
  const encoded = encodeURI(String(raw));
  if (
    encoded.length >= 1 &&
    encoded.length <= PAGE_MAX_LENGTH &&
    PAGE_PATTERN.test(encoded)
  ) {
    return encoded;
  }
  return '/';
}

export function visitId(crypto) {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

export function viewportRatio(element, window) {
  const rect = element.getBoundingClientRect();
  const area = rect.width * rect.height;
  if (area <= 0) return 0;
  const width = Math.max(0, Math.min(rect.right, window.innerWidth) - Math.max(rect.left, 0));
  const height = Math.max(0, Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0));
  return (width * height) / area;
}

// How to add a new monitored surface:
// 1. Define an adapter below ({ kind, mutationSelector, roots, scan,
//    keysEqual, groupCount, buildPayload, createExtra }) and register it.
// 2. Render a `<script class="impressions-config" id="<config-id>" ...>`
//    tag in theme.liquid with data-endpoint/data-env.
// 3. Add one entry to BOOTSTRAP. Lifecycle and delivery stay shared.
const adapters = new Map();

export function registerAdapter(adapter) {
  adapters.set(adapter.kind, adapter);
}

const ROOT_SELECTOR = '[data-aest-kind="collection"], [data-aest-collection-id]';
const CARD_SELECTOR =
  'product-list > product-card[data-aest-product-id], product-list > .box-cal > product-card[data-aest-product-id]';

export const CollectionAdapter = {
  kind: 'collection',
  mutationSelector:
    'product-card[data-aest-product-id], [data-aest-kind="collection"], [data-aest-collection-id]',

  roots(document) {
    return Array.from(
      document.querySelectorAll(ROOT_SELECTOR),
      (root) => root.closest('.shopify-section') || root,
    );
  },

  scan(document, seen, roots) {
    const current = new Map();
    for (const section of roots) {
      if (!section.isConnected) continue;
      const scope = section.matches(ROOT_SELECTOR)
        ? [section]
        : section.querySelectorAll(ROOT_SELECTOR);
      for (const root of scope) {
        const collectionId = root.dataset.aestCollectionId;
        if (!ID_PATTERN.test(collectionId || '')) continue;
        const rawCollectionSlug = root.dataset.aestCollectionSlug;
        const collectionSlug =
          typeof rawCollectionSlug === 'string' && SLUG_PATTERN.test(rawCollectionSlug)
            ? rawCollectionSlug
            : '';
        for (const card of root.querySelectorAll(CARD_SELECTOR)) {
          const productId = card.dataset.aestProductId;
          if (!ID_PATTERN.test(productId || '') || seen.has(productId)) continue;
          current.set(card, { productId, collectionId, collectionSlug });
        }
      }
    }
    return current;
  },

  keysEqual(a, b) {
    return (
      a.productId === b.productId &&
      a.collectionId === b.collectionId &&
      a.collectionSlug === b.collectionSlug
    );
  },

  // Each batch belongs to one collection; the sender can drain several batches.
  groupCount(queue, batchSize) {
    const collectionId = queue[0].keys.collectionId;
    let count = 0;
    while (
      count < batchSize &&
      count < queue.length &&
      queue[count].keys.collectionId === collectionId
    ) {
      count += 1;
    }
    return count;
  },

  buildPayload(pageViewId, env, extra, items) {
    return {
      page_view_id: pageViewId,
      event_type: 'card_impression',
      collection_id: items[0].keys.collectionId,
      ...(extra?.page ? { page: extra.page } : {}),
      env,
      ...(items[0].keys.collectionSlug ? { collection_slug: items[0].keys.collectionSlug } : {}),
      events: items.map((item) => item.event),
    };
  },

  createExtra(window) {
    return { page: resolvePage(window) };
  },
};

const BANNER_SELECTOR = '[data-aest-kind="banner"], [data-aest-banner][data-aest-product-id]';

export const BannerAdapter = {
  kind: 'banner',
  mutationSelector: BANNER_SELECTOR,

  roots(document) {
    return [document.documentElement];
  },

  scan(document, seen) {
    const current = new Map();
    for (const banner of document.querySelectorAll(BANNER_SELECTOR)) {
      const productId = banner.dataset.aestProductId;
      if (!ID_PATTERN.test(productId || '')) continue;
      const position = Number(banner.dataset.aestPosition);
      if (!Number.isSafeInteger(position) || position <= 0) continue;
      const source = `home-section-${position}`;
      const sectionId = banner.dataset.aestSectionId || source;
      const blockId = banner.dataset.aestBlockId;
      const dedupeKey = JSON.stringify(blockId ? [sectionId, blockId, productId] : [sectionId, productId]);
      if (seen.has(dedupeKey)) continue;
      current.set(banner, { productId, collectionId: source, collectionSlug: source, dedupeKey });
    }
    return current;
  },

  keysEqual(a, b) {
    return a.productId === b.productId && a.collectionId === b.collectionId &&
      a.dedupeKey === b.dedupeKey;
  },

  groupCount(queue, batchSize) {
    return Math.min(batchSize, queue.length);
  },

  buildPayload(pageViewId, env, extra, items) {
    return {
      page_view_id: pageViewId,
      event_type: 'banner_impression',
      collection_id: items[0].keys.collectionId,
      collection_slug: items[0].keys.collectionSlug,
      page: extra.page,
      env,
      events: items.map((item) => item.event),
    };
  },

  createExtra(window) {
    return { page: resolvePage(window) };
  },
};

function sectionCardsAdapter(kind, sourcePrefix, byBlock) {
  const selector = `[data-aest-kind="${kind}"]`;
  const cardSelector = byBlock
    ? '[data-aest-block-id][data-aest-product-id]'
    : 'product-card[data-aest-product-id]';
  return {
    kind,
    mutationSelector: `${selector}, ${cardSelector}`,

    roots(document) {
      return Array.from(document.querySelectorAll(selector));
    },

    scan(document, seen, roots) {
      const current = new Map();
      for (const root of roots) {
        if (!root.isConnected) continue;
        const sectionId = root.dataset.aestSectionId;
        const position = Number(root.dataset.aestSectionPosition);
        if (!sectionId || !Number.isSafeInteger(position) || position <= 0) continue;
        const source = `${sourcePrefix}-section-${position}`;
        for (const card of root.querySelectorAll(cardSelector)) {
          if (card.closest(selector) !== root) continue;
          const productId = card.dataset.aestProductId;
          const blockId = card.dataset.aestBlockId;
          if (!ID_PATTERN.test(productId || '') || (byBlock && !blockId)) continue;
          const dedupeKey = JSON.stringify(byBlock ? [sectionId, blockId, productId] : [sectionId, productId]);
          if (seen.has(dedupeKey)) continue;
          current.set(card, { productId, collectionId: source, collectionSlug: source, dedupeKey });
        }
      }
      return current;
    },

    keysEqual: BannerAdapter.keysEqual,
    groupCount: CollectionAdapter.groupCount,
    buildPayload: CollectionAdapter.buildPayload,
    createExtra: CollectionAdapter.createExtra,
  };
}

export const IndexProductsAdapter = sectionCardsAdapter('index-products', 'home', true);
export const ProductCardCarouselAdapter = sectionCardsAdapter('product-card-carousel', 'pdp', false);

registerAdapter(CollectionAdapter);
registerAdapter(BannerAdapter);
registerAdapter(IndexProductsAdapter);
registerAdapter(ProductCardCarouselAdapter);

// A sender owns delivery, while trackers own visibility and deduplication. Queue
// entries are immutable so DOM/config changes cannot rewrite an in-flight visit.
export class ImpressionSender {
  constructor(window, { isActive, onCapacity } = {}) {
    this.window = window;
    this.isActive = isActive || (() => window.document.visibilityState === 'visible');
    this.onCapacity = onCapacity || (() => {});
    this.queue = [];
    this.requests = new Set();
    this.inFlightBytes = 0;
    this.counts = new Map();
    this.retryDeadlines = new Map();
    this.generation = 0;
    this.sequence = 0;
    this.timer = null;
    this.suspended = false;
  }

  now() { return this.window.performance.now(); }
  get active() { return !this.suspended && this.isActive(); }
  pendingCount(kind) { return this.counts.get(kind) || 0; }

  log(action, items, details = {}) {
    const first = items[0];
    if (!first || first.env !== 'dev') return;
    devLog(this.window, first.env, first.adapter.kind, action, {
      endpoint: first.endpoint,
      page_view_id: first.pageViewId,
      page: first.extra.page,
      collection_id: first.keys.collectionId,
      events: items.map((item) => ({ ...item.event })),
      ...details,
    });
  }

  routeKey(item) { return JSON.stringify([item.endpoint, item.env]); }

  groupKey(item) {
    return JSON.stringify([
      item.endpoint, item.env, item.pageViewId, item.generation, item.adapter.kind,
      item.extra.page || '', item.keys.collectionId || '', item.keys.collectionSlug || '',
    ]);
  }

  body(items) {
    const first = items[0];
    const payload = first.adapter.buildPayload(first.pageViewId, first.env, first.extra, items);
    return new this.window.Blob([JSON.stringify(payload)], { type: CONTENT_TYPE });
  }

  enqueue(input) {
    let item;
    try {
      if (!input || typeof input.adapter?.buildPayload !== 'function' ||
          !input.adapter.kind || input.generation !== this.generation ||
          typeof input.pageViewId !== 'string' || !input.pageViewId ||
          !ID_PATTERN.test(input.event?.product_id || '') ||
          typeof input.endpoint !== 'string' || !input.endpoint) return 'invalid';
      item = Object.freeze({
        adapter: Object.freeze({ ...input.adapter }),
        endpoint: input.endpoint,
        env: input.env === 'dev' ? 'dev' : 'prod',
        pageViewId: input.pageViewId,
        generation: input.generation,
        extra: Object.freeze({ ...(input.extra || {}) }),
        keys: Object.freeze({ ...(input.keys || {}) }),
        event: Object.freeze({ ...input.event }),
        queuedAt: this.now(),
        sequence: this.sequence++,
      });
      if (this.body([item]).size > MAX_BODY_BYTES) return 'invalid';
    } catch {
      return 'invalid';
    }
    const kind = item.adapter.kind;
    if (this.pendingCount(kind) >= QUEUE_LIMIT) {
      this.log('queue_full', [item], { pending: this.pendingCount(kind) });
      return 'full';
    }
    this.counts.set(kind, this.pendingCount(kind) + 1);
    this.queue.push(item);
    this.log('queued', [item], { pending: this.pendingCount(kind) });
    this.scheduleFlush();
    return 'accepted';
  }

  clearTimer() {
    if (this.timer !== null) this.window.clearTimeout(this.timer);
    this.timer = null;
  }

  resetVisit(generation) {
    this.clearTimer();
    this.generation = generation;
    this.queue = [];
    this.counts.clear();
    // Old requests retain their own body and ledger reservation until settled.
  }

  scheduleFlush() {
    this.clearTimer();
    if (!this.active || this.requests.size || !this.queue.length) return;
    const groups = new Map();
    for (const item of this.queue) {
      const key = this.groupKey(item);
      const group = groups.get(key) || { first: item, count: 0 };
      group.count += 1;
      groups.set(key, group);
    }
    let due = Infinity;
    for (const { first, count } of groups.values()) {
      const ready = count >= BATCH_SIZE ? this.now() : first.queuedAt + FLUSH_MS;
      due = Math.min(due, Math.max(ready, this.retryDeadlines.get(this.routeKey(first)) || 0));
    }
    const delay = Math.max(0, due - this.now());
    if (delay === 0) this.flush();
    else this.timer = this.window.setTimeout(() => { this.timer = null; this.flush(); }, delay);
  }

  releaseItems(items) {
    const kinds = new Set();
    for (const item of items) {
      if (item.generation !== this.generation) continue;
      const kind = item.adapter.kind;
      this.counts.set(kind, Math.max(0, this.pendingCount(kind) - 1));
      kinds.add(kind);
    }
    for (const kind of kinds) this.onCapacity(kind);
  }

  takeBatch() {
    const budget = Math.min(MAX_BODY_BYTES, KEEPALIVE_BYTES - this.inFlightBytes);
    if (budget <= 0) return null;
    // A delayed endpoint does not block another endpoint's independent queue.
    const checked = new Set();
    for (const first of this.queue.slice()) {
      if ((this.retryDeadlines.get(this.routeKey(first)) || 0) > this.now()) continue;
      const key = this.groupKey(first);
      if (checked.has(key)) continue;
      checked.add(key);
      let candidates = this.queue.filter((item) => this.groupKey(item) === key).slice(0, BATCH_SIZE);
      if (typeof first.adapter.groupCount === 'function') {
        const count = first.adapter.groupCount(candidates, BATCH_SIZE);
        if (Number.isSafeInteger(count) && count > 0) candidates = candidates.slice(0, count);
      }
      while (candidates.length) {
        let body;
        try { body = this.body(candidates); } catch { body = null; }
        if (body && body.size <= budget) {
          const selected = new Set(candidates);
          this.queue = this.queue.filter((item) => !selected.has(item));
          return { items: candidates, body };
        }
        if (candidates.length === 1) {
          // A smaller remaining ledger budget is temporary, not an invalid item.
          if (body && body.size <= MAX_BODY_BYTES) break;
          const bad = candidates[0];
          this.queue = this.queue.filter((item) => item !== bad);
          this.releaseItems([bad]);
        }
        candidates.pop();
      }
    }
    return null;
  }

  retryDelay(response) {
    let delay = RETRY_MS;
    try {
      const value = response?.headers?.get('Retry-After');
      if (value) {
        const seconds = Number(value);
        const until = Number.isFinite(seconds) && seconds >= 0
          ? seconds * 1000
          : this.window.Date.parse(value) - this.window.Date.now();
        if (Number.isFinite(until)) delay = Math.max(delay, until);
      }
    } catch { /* Cross-origin response headers may be unavailable. */ }
    return delay;
  }

  armWatchdog(request) {
    if (!this.active || request.finished || request.timedOut || request.timer !== null) return;
    request.timer = this.window.setTimeout(() => {
      request.timer = null;
      request.timedOut = true;
      this.log('timeout', request.items, { timeout_ms: REQUEST_TIMEOUT_MS });
      // Abort is a cancellation request. The fetch/body promise still owns its
      // ledger reservation until it actually settles.
      request.controller.abort();
    }, REQUEST_TIMEOUT_MS);
  }

  finish(request, retry, response) {
    if (request.finished) return;
    request.finished = true;
    if (request.timer !== null) this.window.clearTimeout(request.timer);
    request.timer = null;
    this.requests.delete(request);
    this.inFlightBytes = Math.max(0, this.inFlightBytes - request.body.size);
    const status = response?.status || 0;
    if (request.generation === this.generation) {
      if (retry) {
        const route = this.routeKey(request.items[0]);
        this.retryDeadlines.set(route, Math.max(
          this.retryDeadlines.get(route) || 0, this.now() + this.retryDelay(response),
        ));
        this.queue.push(...request.items);
        this.queue.sort((a, b) => a.sequence - b.sequence);
        this.log('retry', request.items, {
          status,
          retry_in_ms: Math.max(0, this.retryDeadlines.get(route) - this.now()),
        });
      } else {
        this.log(status >= 200 && status < 300 ? 'accepted' : 'rejected', request.items, { status });
        this.releaseItems(request.items);
      }
    } else this.log('stale_response', request.items, { status });
    if (this.active) this.scheduleFlush();
    else this.flushExit();
  }

  send(batch) {
    const request = {
      ...batch,
      generation: batch.items[0].generation,
      controller: new this.window.AbortController(),
      timer: null,
      finished: false,
      timedOut: false,
    };
    this.requests.add(request);
    this.inFlightBytes += batch.body.size;
    this.log('send', request.items, {
      bytes: batch.body.size,
      in_flight_bytes: this.inFlightBytes,
    });
    this.armWatchdog(request);
    try {
      Promise.resolve(this.window.fetch(batch.items[0].endpoint, {
        method: 'POST',
        headers: { 'Content-Type': CONTENT_TYPE },
        credentials: 'omit',
        keepalive: true,
        body: batch.body,
        signal: request.controller.signal,
      })).then(async (response) => {
        // Fetch resolves at headers. Keep the reservation until the body finishes.
        if (typeof response?.arrayBuffer === 'function') await response.arrayBuffer();
        else if (typeof response?.text === 'function') await response.text();
        const status = response?.status || 0;
        const terminal = (status >= 200 && status < 300) ||
          (status >= 400 && status < 500 && status !== 408 && status !== 429);
        this.finish(request, request.timedOut || !terminal, response);
      }).catch(() => this.finish(request, true));
    } catch {
      // Defer settlement so an immediately throwing fetch cannot recurse through
      // the hidden drain loop before its retry deadline has been installed.
      this.window.queueMicrotask(() => this.finish(request, true));
    }
  }

  flush() {
    this.clearTimer();
    if (!this.active || this.requests.size) return;
    const batch = this.takeBatch();
    if (batch) this.send(batch);
    else if (this.queue.length) this.scheduleFlush();
  }

  flushExit() {
    this.clearTimer();
    let batch;
    while ((batch = this.takeBatch())) this.send(batch);
  }

  suspend() {
    this.suspended = true;
    this.clearTimer();
    for (const request of this.requests) {
      if (request.timer !== null) this.window.clearTimeout(request.timer);
      request.timer = null;
    }
    this.flushExit();
  }

  resume() {
    this.suspended = false;
    for (const request of this.requests) this.armWatchdog(request);
    this.scheduleFlush();
  }
}

const RUNTIME_KEY = '__aestImpressionsRuntime';
const CONFIG_SELECTOR = '.impressions-config, #collection-impressions-config, #banner-impressions-config';
const MUTATION_ATTRIBUTES = [
  'data-aest-kind', 'data-aest-banner', 'data-aest-product-id', 'data-aest-product-name',
  'data-aest-product-slug', 'data-aest-position', 'data-aest-collection-id',
  'data-aest-collection-slug', 'data-aest-section-id', 'data-aest-section-position',
  'data-aest-block-id', 'id', 'data-endpoint', 'data-env',
];

function capable(window) {
  return typeof window.IntersectionObserver === 'function' &&
    typeof window.MutationObserver === 'function' &&
    (typeof window.crypto?.randomUUID === 'function' ||
      typeof window.crypto?.getRandomValues === 'function');
}

export class ImpressionRuntime {
  constructor(window) {
    this.window = window;
    this.document = window.document;
    this.trackers = new Map();
    this.pageHidden = false;
    this.generation = 1;
    this.pageViewId = visitId(window.crypto);
    this.refreshPending = false;
    this.sender = new ImpressionSender(window, {
      isActive: () => this.active,
      onCapacity: (kind) => this.trackers.get(kind)?.onCapacity(),
    });
    this.sender.resetVisit(this.generation);
    this.mutations = new window.MutationObserver((records) => {
      if (this.consumeMutations(records)) this.scheduleRefresh();
    });
    this.mutations.observe(this.document, {
      childList: true, subtree: true, attributes: true,
      attributeFilter: MUTATION_ATTRIBUTES,
    });
    this.document.addEventListener('shopify:section:load', () => this.scheduleRefresh());
    this.document.addEventListener('visibilitychange', () => {
      if (!this.active) this.suspend();
      else this.resume();
    });
    window.addEventListener('pagehide', () => {
      this.pageHidden = true;
      this.suspend();
    });
    window.addEventListener('pageshow', (event) => {
      this.pageHidden = false;
      if (event.persisted) this.resetVisit();
      this.resume();
    });
  }

  get active() { return !this.pageHidden && this.document.visibilityState === 'visible'; }

  consumeMutations(records) {
    const removed = records.flatMap((record) => Array.from(record.removedNodes || []))
      .filter((node) => typeof node.contains === 'function');
    let interrupted = false;
    if (removed.length) {
      for (const tracker of this.trackers.values()) {
        for (const element of tracker.tracked.keys()) {
          if (!removed.some((node) => node === element || node.contains(element))) continue;
          // Current DOM membership alone cannot reveal a remove/reinsert in the
          // same mutation turn. A new observation must earn a fresh full dwell.
          tracker.forget(element);
          interrupted = true;
        }
      }
    }
    return interrupted || this.relevant(records);
  }

  relevant(records) {
    const selector = [CONFIG_SELECTOR, ...Array.from(adapters.values(), (adapter) => adapter.mutationSelector)]
      .filter(Boolean).join(', ');
    return records.some((record) => {
      if (record.type === 'attributes') {
        if (record.attributeName === 'id' || record.attributeName === 'data-endpoint' ||
            record.attributeName === 'data-env') {
          return record.target.tagName === 'SCRIPT';
        }
        return true;
      }
      return [...record.addedNodes, ...record.removedNodes].some((node) =>
        node.nodeType === 1 &&
        (node.matches(selector) || node.querySelector(selector)));
    });
  }

  scheduleRefresh() {
    if (this.refreshPending) return;
    this.refreshPending = true;
    this.window.queueMicrotask(() => {
      if (!this.refreshPending) return;
      this.refreshPending = false;
      this.refresh();
    });
  }

  drainMutations() {
    const changed = this.consumeMutations(this.mutations.takeRecords());
    if (changed || this.refreshPending) {
      this.refreshPending = false;
      this.refresh();
    }
  }

  refresh() {
    bootstrap(this.window);
    for (const tracker of this.trackers.values()) tracker.refresh();
  }

  resetVisit() {
    this.generation += 1;
    this.pageViewId = visitId(this.window.crypto);
    this.sender.resetVisit(this.generation);
    for (const tracker of this.trackers.values()) tracker.resetVisit();
  }

  suspend() {
    for (const tracker of this.trackers.values()) tracker.suspend();
    this.sender.suspend();
  }

  resume() {
    if (!this.active) return;
    this.drainMutations();
    for (const tracker of this.trackers.values()) tracker.resume();
    this.sender.resume();
  }
}

function getRuntime(window) {
  if (!window[RUNTIME_KEY]) window[RUNTIME_KEY] = new ImpressionRuntime(window);
  return window[RUNTIME_KEY];
}

function productMetadata(element) {
  return {
    productName: element.dataset.aestProductName || '',
    productSlug: element.dataset.aestProductSlug || '',
    position: element.dataset.aestPosition || '',
  };
}

function metadataEqual(a, b) {
  return a.productName === b.productName && a.productSlug === b.productSlug && a.position === b.position;
}

function dedupeKey(keys) {
  return keys.dedupeKey ?? keys.productId;
}

export class ImpressionTracker {
  constructor(window, endpoint, env, adapter, extra) {
    this.window = window;
    this.document = window.document;
    this.endpoint = new URL(endpoint).href;
    this.env = env === 'dev' ? 'dev' : 'prod';
    this.adapter = adapter;
    this.extra = Object.freeze({ ...(extra || adapter.createExtra?.(window) || {}) });
    this.tracked = new Map();
    this.seen = new Set();
    this.runtime = getRuntime(window);
    this.sender = this.runtime.sender;
    this.intersections = new window.IntersectionObserver(
      (entries) => this.onIntersections(entries),
      { threshold: [0, 0.5], rootMargin: '0px' },
    );
    this.runtime.trackers.set(adapter.kind, this);
    this.refresh();
    devLog(window, this.env, adapter.kind, 'initialized', {
      endpoint: this.endpoint,
      page_view_id: this.pageViewId,
      page: this.extra.page,
      targets: this.tracked.size,
    });
  }

  get active() { return this.runtime.active; }
  get generation() { return this.runtime.generation; }
  get pageViewId() { return this.runtime.pageViewId; }
  get pageHidden() { return this.runtime.pageHidden; }
  get queue() { return this.sender.queue.filter((item) => item.adapter.kind === this.adapter.kind); }

  cancelDwell(state) {
    if (state.pending) this.window.clearTimeout(state.pending.timer);
    state.pending = null;
  }

  forget(element) {
    const state = this.tracked.get(element);
    if (state) this.cancelDwell(state);
    this.intersections.unobserve(element);
    this.tracked.delete(element);
  }

  resetVisit() {
    this.intersections.disconnect();
    for (const state of this.tracked.values()) this.cancelDwell(state);
    this.tracked.clear();
    this.seen.clear();
  }

  refresh() {
    this.roots = this.adapter.roots(this.document);
    const current = this.adapter.scan(this.document, this.seen, this.roots);
    let changed = false;
    for (const [element, state] of this.tracked) {
      const keys = current.get(element);
      if (!keys || !this.adapter.keysEqual(keys, state) ||
          !metadataEqual(productMetadata(element), state)) {
        this.forget(element);
        changed = true;
      }
    }
    for (const [element, keys] of current) {
      if (this.tracked.has(element)) continue;
      this.tracked.set(element, {
        ...keys, ...productMetadata(element), ratio: 0, pending: null,
        waitingCapacity: false, invalid: false,
      });
      changed = true;
      if (this.active) this.intersections.observe(element);
    }
    if (changed && this.env === 'dev') devLog(this.window, this.env, this.adapter.kind, 'targets_updated', {
      targets: this.tracked.size,
      product_ids: Array.from(this.tracked.values(), (state) => state.productId),
    });
  }

  startDwell(element, state) {
    if (!this.active || state.pending || state.waitingCapacity || state.invalid ||
        state.ratio < 0.5 || !element.isConnected) return;
    const pending = { generation: this.generation, timer: null };
    state.pending = pending;
    pending.timer = this.window.setTimeout(() => this.record(element, state, pending), DWELL_MS);
  }

  onIntersections(entries) {
    for (const entry of entries) {
      const state = this.tracked.get(entry.target);
      if (!state) continue;
      state.ratio = entry.isIntersecting ? entry.intersectionRatio : 0;
      if (!this.active || state.ratio < 0.5 || !entry.target.isConnected) this.cancelDwell(state);
      else this.startDwell(entry.target, state);
    }
  }

  record(element, state, pending) {
    // Drain both observers before committing the identity that earned this dwell.
    this.runtime.drainMutations();
    this.onIntersections(this.intersections.takeRecords());
    if (state.pending !== pending) return;
    this.cancelDwell(state);
    const current = this.adapter.scan(this.document, this.seen, this.adapter.roots(this.document)).get(element);
    if (pending.generation !== this.generation || !this.active || !element.isConnected ||
        this.tracked.get(element) !== state || this.seen.has(dedupeKey(state)) ||
        state.ratio < 0.5 || viewportRatio(element, this.window) < 0.5 || !current ||
        !this.adapter.keysEqual(current, state) || !metadataEqual(productMetadata(element), state)) {
      this.refresh();
      return;
    }
    const position = Number(element.dataset.aestPosition);
    const name = (element.dataset.aestProductName || '').trim();
    const slug = element.dataset.aestProductSlug;
    const result = this.sender.enqueue({
      adapter: this.adapter, endpoint: this.endpoint, env: this.env,
      pageViewId: this.pageViewId, generation: this.generation,
      extra: this.extra, keys: current,
      event: {
        product_id: state.productId,
        occurred_at: this.window.Date.now(),
        ...(Number.isSafeInteger(position) && position > 0 ? { position } : {}),
        ...(name.length > 0 && name.length <= NAME_MAX_LENGTH ? { name } : {}),
        ...(typeof slug === 'string' && SLUG_PATTERN.test(slug) ? { slug } : {}),
      },
    });
    if (result === 'accepted') {
      const key = dedupeKey(state);
      this.seen.add(key);
      for (const [other, keys] of this.tracked) {
        if (dedupeKey(keys) === key) this.forget(other);
      }
    } else if (result === 'full') state.waitingCapacity = true;
    else {
      state.invalid = true;
      devLog(this.window, this.env, this.adapter.kind, 'invalid_event', { product_id: state.productId });
    }
  }

  onCapacity() {
    if (!this.active || this.sender.pendingCount(this.adapter.kind) >= QUEUE_LIMIT) return;
    this.runtime.drainMutations();
    this.onIntersections(this.intersections.takeRecords());
    for (const [element, state] of this.tracked) {
      if (!state.waitingCapacity) continue;
      state.waitingCapacity = false;
      if (viewportRatio(element, this.window) >= 0.5) this.startDwell(element, state);
    }
  }

  suspend() {
    this.intersections.disconnect();
    for (const state of this.tracked.values()) {
      this.cancelDwell(state);
      state.ratio = 0;
    }
  }

  resume() {
    if (!this.active) return;
    this.suspend();
    this.refresh();
    for (const [element, state] of this.tracked) {
      if (this.sender.pendingCount(this.adapter.kind) < QUEUE_LIMIT) state.waitingCapacity = false;
      this.intersections.observe(element);
    }
  }

  flush() { this.sender.flush(); }
  flushExit() { this.sender.flushExit(); }
}

export function initImpressions(window, { configId, globalKey, kind }) {
  if (window[globalKey]) return window[globalKey];
  const adapter = adapters.get(kind);
  const config = window.document.getElementById(configId);
  // Theme editor is intentional dev traffic (Liquid renders data-env="dev");
  // loopback is forced to dev in the env resolution below.
  if (
    !adapter ||
    !config ||
    !capable(window)
  ) {
    return null;
  }
  try {
    // `shopify theme dev` is a proxy: Liquid never sees localhost, so force dev
    // client-side. The theme checkbox gates configuration in every environment.
    const hostname = window.location?.hostname;
    const isLoopback = hostname === 'localhost' || hostname === '127.0.0.1';
    const endpoint = new URL(config.dataset.endpoint);
    const secureProtocol =
      endpoint.protocol === 'https:' ||
      (isLoopback && endpoint.protocol === 'http:');
    if (!secureProtocol || endpoint.username || endpoint.password) return null;
    const env =
      config.dataset.env === 'dev' || isLoopback
        ? 'dev'
        : 'prod';
    const runtime = getRuntime(window);
    const existing = runtime.trackers.get(kind);
    if (existing) {
      window[globalKey] = existing;
      return existing;
    }
    const tracker = new ImpressionTracker(
      window,
      endpoint.href,
      env,
      adapter,
      adapter.createExtra?.(window) ?? {},
    );
    window[globalKey] = tracker;
    return tracker;
  } catch {
    // An analytics configuration/capability failure must not break the shop.
    return null;
  }
}

export function initCollectionImpressions(window) {
  return initImpressions(window, {
    configId: 'collection-impressions-config',
    globalKey: '__aestCollectionImpressions',
    kind: 'collection',
  });
}

export function initBannerImpressions(window) {
  return initImpressions(window, {
    configId: 'banner-impressions-config',
    globalKey: '__aestBannerImpressions',
    kind: 'banner',
  });
}

export function initIndexProductsImpressions(window) {
  return initImpressions(window, {
    configId: 'index-products-impressions-config',
    globalKey: '__aestIndexProductsImpressions',
    kind: 'index-products',
  });
}

export function initProductCardCarouselImpressions(window) {
  return initImpressions(window, {
    configId: 'product-card-carousel-impressions-config',
    globalKey: '__aestProductCardCarouselImpressions',
    kind: 'product-card-carousel',
  });
}

const BOOTSTRAP = [
  {
    configId: 'collection-impressions-config',
    globalKey: '__aestCollectionImpressions',
    kind: 'collection',
  },
  {
    configId: 'banner-impressions-config',
    globalKey: '__aestBannerImpressions',
    kind: 'banner',
  },
  {
    configId: 'index-products-impressions-config',
    globalKey: '__aestIndexProductsImpressions',
    kind: 'index-products',
  },
  {
    configId: 'product-card-carousel-impressions-config',
    globalKey: '__aestProductCardCarouselImpressions',
    kind: 'product-card-carousel',
  },
];

function bootstrap(window) {
  if (!capable(window)) return;
  // The observer also discovers configs inserted after this module was cached.
  getRuntime(window);
  for (const entry of BOOTSTRAP) {
    initImpressions(window, entry);
  }
}

if (typeof window !== 'undefined') {
  if (window.document.readyState === 'loading') {
    window.document.addEventListener(
      'DOMContentLoaded',
      () => bootstrap(window),
      { once: true },
    );
  } else {
    bootstrap(window);
  }
}
